import { QueryGetProductsOrderByColumn, SortOrder } from "generated/graphql";
import { FeatureCollection } from "geojson";
import useGetProducts from "hooks/products/useGetProducts";
import { MapOutput } from "interfaces/interfaces";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GroupedAssignment,
  ProductOption,
} from "modules/proposals/draft/measurements/measurementTypes";
import {
  buildStageMeasurementDrafts,
  ExistingStageMeasurement,
  getMeasurementValueForJob,
  normalizeStageMapData,
  StageMeasurementDraft,
} from "modules/proposals/draft/measurements/stageMeasurements";

export type PersistMeasurementsFn = (
  nextMapData: FeatureCollection | null,
  nextMeasurements: StageMeasurementDraft[],
  options?: { refetch?: boolean }
) => Promise<unknown | null>;

type UseMapMeasurementsArgs = {
  enabled: boolean;
  resetDeps: unknown[];
  mapDataJson: string | null | undefined;
  existingMeasurements: ExistingStageMeasurement[];
  persistMeasurements: PersistMeasurementsFn;
  canPersist: boolean;
  initialDrawerOpen?: boolean | ((measurements: StageMeasurementDraft[]) => boolean);
};

export default function useMapMeasurements({
  enabled,
  resetDeps,
  mapDataJson,
  existingMeasurements,
  persistMeasurements,
  canPersist,
  initialDrawerOpen = false,
}: UseMapMeasurementsArgs) {
  const [mapData, setMapData] = useState<FeatureCollection | null>(null);
  const [measurements, setMeasurements] = useState<StageMeasurementDraft[]>([]);
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [persisting, setPersisting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const {
    data: products,
    loading: productsLoading,
    getAllData,
    paginatorInfo,
  } = useGetProducts({
    first: 200,
    page: 1,
    orderBy: [{ column: QueryGetProductsOrderByColumn.NAME, order: SortOrder.ASC }],
  });

  useEffect(() => {
    if (!productsLoading && paginatorInfo?.total && (products?.length ?? 0) < paginatorInfo.total) {
      getAllData();
    }
  }, [getAllData, paginatorInfo?.total, products?.length, productsLoading]);

  useEffect(() => {
    if (!enabled) return;

    const parsedMapData = mapDataJson ? JSON.parse(mapDataJson) : null;
    const normalizedMapData = normalizeStageMapData(parsedMapData);
    const nextMeasurements = buildStageMeasurementDrafts(normalizedMapData, existingMeasurements);

    setMapData(normalizedMapData);
    setMeasurements(nextMeasurements);
    setSelectedFeatureId(nextMeasurements[0]?.featureId ?? null);
    setIsDrawerOpen(
      typeof initialDrawerOpen === "function"
        ? initialDrawerOpen(nextMeasurements)
        : initialDrawerOpen
    );
    setIsDirty(false);
  }, [enabled, ...resetDeps]);

  const assignedMeasurements = useMemo(
    () => measurements.filter((m) => m.productId),
    [measurements]
  );

  const groupedAssignments = useMemo<GroupedAssignment[]>(
    () =>
      Object.values(
        assignedMeasurements.reduce<Record<string, GroupedAssignment>>((acc, m) => {
          const id = m.productId as string;
          const current = acc[id] ?? {
            productId: id,
            productName: m.productName || "Product",
            total: 0,
            hasScreenshot: false,
          };
          current.total += m.measurementValue ?? 0;
          current.hasScreenshot = current.hasScreenshot || m.isScreenshotTarget;
          acc[id] = current;
          return acc;
        }, {})
      ),
    [assignedMeasurements]
  );

  const persist = useCallback(
    async (
      nextMapData: FeatureCollection | null,
      nextMeasurements: StageMeasurementDraft[],
      options?: { refetch?: boolean }
    ) => {
      const normalizedMapData = normalizeStageMapData(nextMapData);
      if (!normalizedMapData) return null;

      setPersisting(true);
      try {
        const result = await persistMeasurements(nextMapData, nextMeasurements, options);
        setMapData(normalizedMapData);
        setMeasurements(nextMeasurements);
        setIsDirty(false);
        return result;
      } finally {
        setPersisting(false);
      }
    },
    [persistMeasurements]
  );

  const rebuildMeasurements = useCallback(
    (nextMapData: FeatureCollection | null, sourceMeasurements: StageMeasurementDraft[]) => {
      const normalizedMapData = normalizeStageMapData(nextMapData);
      const nextMeasurements = buildStageMeasurementDrafts(
        normalizedMapData,
        sourceMeasurements.map((m) => ({
          geojsonFeatureId: m.featureId,
          measurementJob: m.measurementJob,
          measurementValue: m.measurementValue,
          label: m.label,
          sortOrder: m.sortOrder,
          productId: m.productId,
          isScreenshotTarget: m.isScreenshotTarget,
          product: m.productId
            ? {
                id: m.productId,
                name: m.productName ?? "",
                measurementJob: m.measurementJob,
                dailyUnitsComplete: m.defaultDailyUnitsComplete,
              }
            : null,
        }))
      );
      return { normalizedMapData, nextMeasurements };
    },
    []
  );

  const updateMeasurementProduct = useCallback(
    (featureId: string, product: ProductOption | null) => {
      setMeasurements((current) =>
        current.map((m) => {
          if (m.featureId !== featureId) return m;
          const nextMeasurementJob = product?.measurementJob ?? null;
          return {
            ...m,
            productId: product?.id ?? null,
            productName: product?.name ?? null,
            measurementJob: nextMeasurementJob,
            measurementValue: getMeasurementValueForJob(m.metrics, nextMeasurementJob),
            isScreenshotTarget: product ? m.isScreenshotTarget : false,
            defaultDailyUnitsComplete: product?.dailyUnitsComplete ?? null,
          };
        })
      );
      setIsDirty(true);
    },
    []
  );

  const toggleScreenshot = useCallback((featureId: string, checked: boolean) => {
    setMeasurements((current) =>
      current.map((m) => (m.featureId === featureId ? { ...m, isScreenshotTarget: checked } : m))
    );
    setIsDirty(true);
  }, []);

  const save = useCallback(() => persist(mapData, measurements), [mapData, measurements, persist]);

  // Keep a ref so handleMapChange always reads the latest measurements without
  // needing a new function identity. Map.tsx registers event handlers once on mount
  // and never re-registers them, so a stable callback reference is critical.
  const measurementsRef = useRef(measurements);
  measurementsRef.current = measurements;

  const handleMapChange = useCallback(
    (data: MapOutput) => {
      const current = measurementsRef.current;
      const { normalizedMapData, nextMeasurements } = rebuildMeasurements(data.geojson, current);
      // Map edit events may be emitted faster than React can render. Update this
      // synchronously so a following event preserves the assignments just rebuilt.
      measurementsRef.current = nextMeasurements;
      setMapData(normalizedMapData);
      setMeasurements(nextMeasurements);
      setSelectedFeatureId((prev) => prev ?? nextMeasurements[0]?.featureId ?? null);
      if (nextMeasurements.length > current.length) {
        setSelectedFeatureId(nextMeasurements[nextMeasurements.length - 1]?.featureId ?? null);
      }
      setIsDirty(true);
    },
    [rebuildMeasurements]
  );

  const handleFeatureSelect = useCallback((featureId: string) => {
    setSelectedFeatureId(featureId);
  }, []);

  const toggleDrawer = useCallback(() => setIsDrawerOpen((current) => !current), []);

  return {
    mapData,
    measurements,
    selectedFeatureId,
    isDrawerOpen,
    persisting,
    isDirty,
    products,
    productsLoading,
    canSave: !!mapData && canPersist,
    groupedAssignments,
    assignedMeasurements,
    selectFeature: setSelectedFeatureId,
    updateMeasurementProduct,
    toggleScreenshot,
    save,
    persist,
    handleMapChange,
    handleFeatureSelect,
    toggleDrawer,
  };
}
